// src/components/quiz/EmailCapture.tsx
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { EncryptionDemo } from './EncryptionDemo'
import { WhatIsFemm } from './WhatIsFemm'
import { posthog } from '../../lib/posthog'

interface EmailCaptureProps {
  onSubmit: (email: string, firstName: string) => void
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 720px)').matches
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 720px)')
    const fn = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mq.addEventListener('change', fn)
    return () => mq.removeEventListener('change', fn)
  }, [])
  return isDesktop
}

const COVERS = [
  { icon: '🔬', title: 'Hormone signal mapping',   sub: 'Prolactin · Androgens · Insulin · Thyroid' },
  { icon: '🔄', title: 'Cycle health',              sub: 'Length, variation, and classification' },
  { icon: '📊', title: 'Clinical priority tier',    sub: 'Where you sit on the care continuum' },
  { icon: '💌', title: 'Your personalised results', sub: 'Sent to your inbox with Dr. Nyandia\'s notes' },
]

const TRUST_ITEMS = ['27 questions', '4–6 minutes', 'Clinically informed', 'End-to-end encrypted', 'Never shared']

export function EmailCapture({ onSubmit }: EmailCaptureProps) {
  const [email, setEmail]           = useState('')
  const [firstName, setFirstName]   = useState('')
  const [emailValid, setEmailValid] = useState<boolean | null>(null)
  const isDesktop = useIsDesktop()

  const validateEmail = (val: string) => {
    setEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!emailValid) return
    posthog.capture('quiz_started', {
      variant: posthog.getFeatureFlag('femm-what-is-copy') ?? 'control',
    })
    onSubmit(email, firstName)
  }

  // ── LEFT column content ────────────────────────────────────────────
  const leftContent = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      style={{ display: 'flex', flexDirection: 'column', gap: isDesktop ? '1.75rem' : '1.5rem' }}
    >
      <div>
        {/* IFSD letterhead */}
        <a
          href="https://ifsdnetwork.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.55rem',
            textDecoration: 'none', marginBottom: '1.25rem',
          }}
        >
          <img src="/logo.png" alt="IFSD" style={{ height: '32px', width: 'auto', borderRadius: '6px' }} />
          <span style={{
            fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.35,
            color: 'var(--color-deep-teal)', letterSpacing: '0.01em',
          }}>
            Institute for Family<br />and Social Development
          </span>
        </a>

        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: isDesktop ? 'clamp(1.6rem, 2.5vw, 2.1rem)' : 'clamp(1.5rem, 6vw, 1.9rem)',
          color: 'var(--color-deep-teal)',
          lineHeight: 1.2,
          marginBottom: '0.75rem',
        }}>
          Understand what your hormones are telling you.
        </h1>

        <p style={{
          color: '#475569', fontSize: '0.95rem', lineHeight: 1.65,
          marginBottom: '0.5rem',
        }}>
          A 4–6 minute hormonal health assessment developed by Dr. Nyandia using the FEMM clinical framework.
        </p>

        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '0.88rem', fontWeight: 600,
          color: '#0a6b63',
          letterSpacing: '0.02em',
        }}>
          Free. Confidential. Yours.
        </p>

        <WhatIsFemm />
      </div>

      {/* What this covers */}
      <div>
        <p style={{
          fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.11em',
          textTransform: 'uppercase', color: '#475569',
          marginBottom: '0.85rem',
        }}>
          What this assessment covers
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.625rem',
        }}>
          {COVERS.map(({ icon, title, sub }) => (
            <div key={title} style={{
              background: 'rgba(255,255,255,0.62)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              border: '1px solid rgba(255,255,255,0.55)',
              borderRadius: '12px',
              padding: '0.875rem',
              display: 'flex', flexDirection: 'column', gap: '0.3rem',
            }}>
              <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>{icon}</span>
              <span style={{
                fontSize: '0.8rem', fontWeight: 600,
                color: '#1e293b', lineHeight: 1.3,
              }}>{title}</span>
              <span style={{
                fontSize: '0.72rem', color: '#334155', lineHeight: 1.4,
              }}>{sub}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Encryption demo */}
      <EncryptionDemo />
    </motion.div>
  )

  // ── RIGHT column content ───────────────────────────────────────────
  const rightContent = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: isDesktop ? 0.15 : 0, ease: [0.22, 1, 0.36, 1] }}
      style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
    >
      {/* Form card */}
      <div style={{
        background: 'var(--color-white)',
        borderRadius: 'var(--radius-2xl)',
        padding: '1.875rem',
        boxShadow: 'var(--shadow-lg)',
        border: '1px solid var(--color-border)',
      }}>
        <p style={{
          fontSize: '0.95rem', fontWeight: 600,
          color: 'var(--color-slate)', marginBottom: '0.4rem', lineHeight: 1.4,
        }}>
          Where should we send your results?
        </p>
        <p style={{
          fontSize: '0.82rem', color: '#475569',
          marginBottom: '1.5rem', lineHeight: 1.55,
        }}>
          Your personalised hormonal health profile will be emailed to you along with a short educational series from Dr. Nyandia.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* First name */}
          <div>
            <label htmlFor="firstName" style={{
              display: 'block', fontSize: '0.8rem', fontWeight: 500,
              color: 'var(--color-slate)', marginBottom: '0.4rem',
            }}>
              First name <span style={{ color: '#64748b', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              placeholder="Your first name"
              autoComplete="given-name"
              style={{
                width: '100%', padding: '0.75rem 1rem',
                border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-xl)',
                fontSize: '1rem', color: 'var(--color-slate)',
                background: 'var(--color-cream)', outline: 'none',
                transition: 'border-color 0.2s',
                fontFamily: 'var(--font-sans)',
              }}
              onFocus={e  => (e.target.style.borderColor = 'var(--color-teal)')}
              onBlur={e   => (e.target.style.borderColor = 'var(--color-border)')}
            />
            <p style={{
              fontSize: '0.72rem', color: '#475569',
              marginTop: '0.3rem',
            }}>
              Used to personalise your results email.
            </p>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" style={{
              display: 'block', fontSize: '0.8rem', fontWeight: 500,
              color: 'var(--color-slate)', marginBottom: '0.4rem',
            }}>
              Email address <span style={{ color: 'var(--color-rose)' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => {
                  setEmail(e.target.value)
                  if (e.target.value.length > 3) validateEmail(e.target.value)
                }}
                placeholder="you@example.com"
                required
                autoComplete="email"
                style={{
                  width: '100%',
                  padding: `0.75rem ${emailValid ? '2.5rem' : '1rem'} 0.75rem 1rem`,
                  border: `1.5px solid ${emailValid === false ? 'var(--color-rose)' : emailValid ? 'var(--color-teal)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius-xl)',
                  fontSize: '1rem', color: 'var(--color-slate)',
                  background: 'var(--color-cream)', outline: 'none',
                  transition: 'border-color 0.2s',
                  fontFamily: 'var(--font-sans)',
                }}
              />
              {emailValid && (
                <span style={{
                  position: 'absolute', right: '0.75rem', top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#0a6b63', fontSize: '1rem',
                }}>✓</span>
              )}
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={!emailValid}
            whileHover={emailValid ? { scale: 1.02 } : {}}
            whileTap={emailValid   ? { scale: 0.98 } : {}}
            style={{
              marginTop: '0.25rem',
              padding: '0.9rem 1.5rem',
              background: emailValid ? 'var(--color-deep-teal)' : '#64748b',
              color: 'white', border: 'none',
              borderRadius: 'var(--radius-xl)',
              fontSize: '1rem', fontWeight: 600,
              cursor: emailValid ? 'pointer' : 'not-allowed',
              fontFamily: 'var(--font-sans)',
              transition: 'background 0.2s',
              letterSpacing: '0.01em',
            }}
          >
            Let's begin →
          </motion.button>
        </form>

        {/* In-card privacy note */}
        <div style={{
          marginTop: '1.1rem',
          paddingTop: '1rem',
          borderTop: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem',
          fontSize: '0.72rem', color: '#475569',
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          End-to-end encrypted · No spam · Unsubscribe any time
        </div>
      </div>

      {/* Trust strip */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
        gap: '0.5rem 1.2rem',
        padding: '0 0.25rem',
      }}>
        {TRUST_ITEMS.map(item => (
          <div key={item} style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            fontSize: '0.72rem', color: '#475569',
          }}>
            <span style={{ color: 'var(--color-teal)', fontWeight: 700 }}>✓</span>
            {item}
          </div>
        ))}
      </div>
    </motion.div>
  )

  return (
    <div style={{
      minHeight: '100dvh',
      backgroundImage: "url('/bgd-image-1.jpg')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      position: 'relative',
    }}>
      {/* White overlay — brings all text to WCAG 4.5:1 minimum on image */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'rgba(255,255,255,0.78)',
      }} />

      {isDesktop ? (
        // ── DESKTOP: two-column grid ──────────────────────────────────
        <div style={{
          maxWidth: '960px', margin: '0 auto',
          padding: '3.5rem 2.5rem',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '4rem',
          alignItems: 'start',
          position: 'relative', zIndex: 1,
        }}>
          {/* Left — bare, no card */}
          <div>
            {leftContent}
          </div>

          {/* Right — sticky so form stays in view while left scrolls */}
          <div style={{
            position: 'sticky', top: '2.5rem',
            alignSelf: 'flex-start',
          }}>
            {rightContent}
          </div>
        </div>
      ) : (
        // ── MOBILE: single column ─────────────────────────────────────
        <div style={{
          maxWidth: '480px', margin: '0 auto',
          padding: '2rem 1.25rem 3rem',
          display: 'flex', flexDirection: 'column',
          gap: '1.75rem',
          position: 'relative', zIndex: 1,
        }}>
          {/* Hero (H1 + subtitle) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* IFSD letterhead */}
            <a
              href="https://ifsdnetwork.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.55rem',
                textDecoration: 'none', marginBottom: '1.25rem',
              }}
            >
              <img src="/logo.png" alt="IFSD" style={{ height: '32px', width: 'auto', borderRadius: '6px' }} />
              <span style={{
                fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.35,
                color: 'var(--color-deep-teal)', letterSpacing: '0.01em',
              }}>
                Institute for Family<br />and Social Development
              </span>
            </a>

            <h1 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(1.5rem, 6vw, 1.9rem)',
              color: 'var(--color-deep-teal)',
              lineHeight: 1.2, marginBottom: '0.7rem',
            }}>
              Understand what your hormones are telling you.
            </h1>
            <p style={{
              color: '#475569', fontSize: '0.92rem',
              lineHeight: 1.65, marginBottom: '0.4rem',
            }}>
              A 4–6 minute hormonal health assessment developed by Dr. Nyandia using the FEMM clinical framework.
            </p>
            <p style={{
              fontSize: '0.88rem', fontWeight: 600,
              color: '#0a6b63', letterSpacing: '0.02em',
            }}>
              Free. Confidential. Yours.
            </p>

            <WhatIsFemm />
          </motion.div>

          {/* Form — high on mobile so it's reachable quickly */}
          {rightContent}

          {/* Discovery grid */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <p style={{
              fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.11em',
              textTransform: 'uppercase', color: '#475569',
              marginBottom: '0.85rem',
            }}>
              What this assessment covers
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.625rem' }}>
              {COVERS.map(({ icon, title, sub }) => (
                <div key={title} style={{
                  background: 'rgba(255,255,255,0.62)',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                  border: '1px solid rgba(255,255,255,0.55)',
                  borderRadius: '12px', padding: '0.875rem',
                  display: 'flex', flexDirection: 'column', gap: '0.3rem',
                }}>
                  <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>{icon}</span>
                  <span style={{
                    fontSize: '0.8rem', fontWeight: 600,
                    color: '#1e293b', lineHeight: 1.3,
                  }}>{title}</span>
                  <span style={{
                    fontSize: '0.72rem', color: '#334155', lineHeight: 1.4,
                  }}>{sub}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Encryption demo */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <EncryptionDemo />
          </motion.div>
        </div>
      )}
    </div>
  )
}
